import React, { useEffect, useState } from 'react';
import { hrAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ClipboardList, Download, Eye, FileText, Filter, X } from 'lucide-react';

const resultLabel = (r) => r === 'satisfactory' ? 'Satisfactory' : r === 'non_satisfactory' ? 'Non-Satisfactory' : '—';

export default function CompletedEvaluationsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emps, setEmps] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [filters, setFilters] = useState({ employee_id: '', training_id: '', date_from: '', date_to: '' });
  const [view, setView] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      setRows((await hrAPI.completedEvaluations(params)).data || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    (async () => {
      try {
        const [e, t] = await Promise.all([hrAPI.listEmployees(), hrAPI.listTrainings()]);
        setEmps(e.data || []); setTrainings(t.data || []);
      } catch { /* noop */ }
      load();
    })();
    // eslint-disable-next-line
  }, []);

  const clearFilters = () => { setFilters({ employee_id: '', training_id: '', date_from: '', date_to: '' }); setTimeout(load, 0); };

  const download = async (r) => {
    try {
      const res = await hrAPI.sessionRecord(r.session_id, r.employee_id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `F-HR-05_${r.employee_name.replace(/\s+/g, '_')}_${r.session_id.slice(0, 8)}.docx`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || 'Download failed'); }
  };

  const viewAtt = async (r, att) => {
    try {
      const res = await hrAPI.evalAttachment(r.session_id, r.employee_id, att.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: att.content_type }));
      window.open(url, '_blank'); setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error('Failed to open'); }
  };

  return (
    <div className="space-y-5" data-testid="hr-completed-evals-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-[#0F172A] flex items-center gap-2"><ClipboardList className="w-6 h-6 text-pink-600" /> Completed Evaluations</h1>
        <p className="text-sm text-slate-500 mt-0.5">Browse finished training evaluations. Filter by employee, training or training date.</p>
      </div>

      <Card className="border border-slate-200 shadow-none"><CardContent className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
          <div>
            <Label className="text-xs">Employee</Label>
            <Select value={filters.employee_id || '__all__'} onValueChange={v => setFilters({ ...filters, employee_id: v === '__all__' ? '' : v })}>
              <SelectTrigger data-testid="ce-filter-employee"><SelectValue placeholder="All employees" /></SelectTrigger>
              <SelectContent><SelectItem value="__all__">All employees</SelectItem>{emps.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Training</Label>
            <Select value={filters.training_id || '__all__'} onValueChange={v => setFilters({ ...filters, training_id: v === '__all__' ? '' : v })}>
              <SelectTrigger data-testid="ce-filter-training"><SelectValue placeholder="All trainings" /></SelectTrigger>
              <SelectContent><SelectItem value="__all__">All trainings</SelectItem>{trainings.map(t => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Date from</Label><Input type="date" value={filters.date_from} onChange={e => setFilters({ ...filters, date_from: e.target.value })} data-testid="ce-filter-from" /></div>
          <div><Label className="text-xs">Date to</Label><Input type="date" value={filters.date_to} onChange={e => setFilters({ ...filters, date_to: e.target.value })} data-testid="ce-filter-to" /></div>
          <div className="flex gap-2">
            <Button className="bg-pink-600 hover:bg-pink-700 text-white gap-1 flex-1" onClick={load} data-testid="ce-apply"><Filter className="w-4 h-4" /> Apply</Button>
            <Button variant="outline" onClick={clearFilters} data-testid="ce-clear"><X className="w-4 h-4" /></Button>
          </div>
        </div>
      </CardContent></Card>

      <Card className="border border-slate-200 shadow-none"><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow className="bg-slate-50/80">
            <TableHead className="text-xs">Employee</TableHead><TableHead className="text-xs">Training</TableHead>
            <TableHead className="text-xs">Date</TableHead><TableHead className="text-xs">Result</TableHead>
            <TableHead className="text-xs">Evaluated By</TableHead><TableHead className="text-xs text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8 text-sm">Loading...</TableCell></TableRow> :
              rows.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8 text-sm">No completed evaluations for the selected filters</TableCell></TableRow> :
                rows.map((r) => (
                  <TableRow key={`${r.session_id}-${r.employee_id}`} data-testid={`ce-row-${r.session_id}-${r.employee_id}`}>
                    <TableCell className="text-sm font-medium">{r.employee_name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{r.training_title} {r.is_group && <Badge variant="outline" className="text-[9px] ml-1">Group</Badge>}</TableCell>
                    <TableCell className="text-xs font-mono">{r.actual_date || '—'}</TableCell>
                    <TableCell><Badge variant="outline" className={`text-[10px] ${r.result === 'satisfactory' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{resultLabel(r.result)}</Badge></TableCell>
                    <TableCell className="text-xs text-slate-600">{r.evaluated_by || '—'}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => setView(r)} data-testid={`ce-view-${r.session_id}-${r.employee_id}`}><Eye className="w-3.5 h-3.5" /> View</Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => download(r)} data-testid={`ce-download-${r.session_id}-${r.employee_id}`}><Download className="w-3.5 h-3.5" /> F/HR/05</Button>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      {/* Read-only detail dialog */}
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ce-view-dialog">
          <DialogHeader><DialogTitle>Evaluation — {view?.employee_name}</DialogTitle></DialogHeader>
          {view && (
            <div className="space-y-2.5 text-sm">
              <Detail label="Training" value={view.training_title} />
              <Detail label="Training date" value={view.actual_date} />
              <Detail label="Test score" value={view.test_score != null ? `${view.test_score}/${view.test_total}` : '—'} />
              <Detail label="Result" value={resultLabel(view.result)} />
              <Detail label="Update skill matrix immediately" value={view.update_immediately == null ? '—' : (view.update_immediately ? 'Yes' : 'No')} />
              <Detail label="Objective evidence" value={view.objective_evidence || '—'} />
              <Detail label="Certificates" value={view.certificates || '—'} />
              {view.result === 'non_satisfactory' && <Detail label="Correction plan" value={view.correction_plan || '—'} />}
              <Detail label="Supervisor comment" value={view.supervisor_comment || '—'} />
              <Detail label="Evaluated by" value={`${view.evaluated_by || '—'}${view.evaluated_at ? ' · ' + view.evaluated_at.slice(0, 10) : ''}`} />
              <div>
                <p className="text-xs font-medium text-slate-500">Attachments</p>
                {(view.attachments || []).length === 0 ? <p className="text-xs text-slate-400">None</p> :
                  <div className="mt-1 space-y-1">
                    {view.attachments.map(att => (
                      <button key={att.id} onClick={() => viewAtt(view, att)} className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1 w-full text-left hover:text-pink-700" data-testid={`ce-att-${att.id}`}>
                        <FileText className="w-3.5 h-3.5 text-slate-500" /><span className="truncate flex-1">{att.filename}</span>
                        <Badge variant="outline" className="text-[9px]">{att.category}</Badge>
                      </button>
                    ))}
                  </div>}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setView(null)}>Close</Button>
            {view && <Button className="bg-pink-600 hover:bg-pink-700 text-white gap-1" onClick={() => download(view)} data-testid="ce-view-download"><Download className="w-4 h-4" /> Download F/HR/05</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Detail = ({ label, value }) => (
  <div className="flex gap-2">
    <span className="text-xs font-medium text-slate-500 w-44 flex-shrink-0">{label}</span>
    <span className="text-sm text-slate-800 whitespace-pre-wrap">{value}</span>
  </div>
);
